import { useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIsFocused } from 'expo-router';
import { supabase } from '@/data/client';
export default function useCheckinRealtime({ eventInstance, eventInstanceLoading, addListAttendance, removeListAttendance, updateListAttendance, onConnectionLost, onConnectionRestored, selectedFilter, realtimeEnabled }) {
    const queryClient = useQueryClient();
    const isFocused = useIsFocused();
    const [isConnected, setIsConnected] = useState(true);
    const hasNotifiedConnectionLost = useRef(false);
    const hasNotifiedConnectionRestored = useRef(true);
    const channelRef = useRef(null);
    const isUnmounting = useRef(false);
    // Generation counter: setup() awaits setAuth, so a stale setup from a
    // previous effect run could resume after the new run reset isUnmounting
    // and subscribe a second channel. Comparing generations aborts it.
    const setupGeneration = useRef(0);
    // Handlers read the filter from a ref so changing it doesn't tear down and
    // rebuild the websocket channel on every selection.
    const selectedFilterRef = useRef(selectedFilter);

    useEffect(() => {
        selectedFilterRef.current = selectedFilter;
    }, [selectedFilter]);

    useEffect(() => {
        if (eventInstanceLoading || !eventInstance?.id || !realtimeEnabled || !isFocused) {
            if ((!realtimeEnabled || !isFocused) && channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
            }
            return;
        }

        // Catch up on attendance changes missed while the screen was unfocused —
        // same key the broadcast handlers below write to.
        queryClient.invalidateQueries({ queryKey: ['attendance', eventInstance.id] });

        const generation = ++setupGeneration.current;

        const setup = async () => {
            // Reset the flag set by a previous cleanup so connection callbacks
            // fire again for this subscription.
            isUnmounting.current = false;
            await supabase.realtime.setAuth();

            // If the effect was cleaned up or superseded by a newer run while
            // setAuth was in flight, don't create a channel the cleanup will
            // never remove.
            if (isUnmounting.current || generation !== setupGeneration.current) return;

            // Always create a fresh channel. removeChannel() is async, so
            // getChannels() can return a channel mid-removal — adding .on
            // bindings to it would accumulate duplicate handlers and an extra
            // subscribe(). Remove any stale same-topic channel instead.
            const stale = supabase.getChannels()?.find(c => c?.subTopic === `CheckIn:${eventInstance.id}`);
            if (stale) supabase.removeChannel(stale);

            const channel = supabase.channel(`CheckIn:${eventInstance.id}`, {
                config: { private: true },
            });

            channel
                .on('broadcast', { event: 'INSERT' }, (msg) => {
                    const record = msg?.payload?.record;
                    if (!record) return;
                    // Skip attendance cache update for Checked In/Not Checked In filters to avoid
                    // triggering processData rerender. addListAttendance handles the list update directly.
                    const shouldSkipCacheUpdate = selectedFilterRef.current === "Checked In" || selectedFilterRef.current === "Not Checked In";
                    if (!shouldSkipCacheUpdate) {
                        queryClient.setQueryData(['attendance', eventInstance.id], old => {
                            return [...(old?.filter(item => item?.attendeeID !== record.attendeeID) || []), record];
                        });
                    }
                    // Update availablePeople for non-Awaiting Print filters
                    addListAttendance(record);
                })
                .on('broadcast', { event: 'UPDATE' }, (msg) => {
                    const record = msg?.payload?.record;
                    if (record) {
                        const shouldSkipCacheUpdate = selectedFilterRef.current === "Checked In" || selectedFilterRef.current === "Not Checked In";
                        if (!shouldSkipCacheUpdate) {
                            queryClient.setQueryData(['attendance', eventInstance.id], old => {
                                return [...(old?.filter(item => item.attendeeID !== record.attendeeID) || []), record];
                            });
                        }
                        // Propagate labelPrinted and other attendance changes to availablePeople
                        if (updateListAttendance) updateListAttendance(record);
                    }
                })
                .on('broadcast', { event: 'DELETE' }, (msg) => {
                    const record = msg?.payload?.old_record;
                    if (record) {
                        const shouldSkipCacheUpdate = selectedFilterRef.current === "Checked In" || selectedFilterRef.current === "Not Checked In";
                        if (!shouldSkipCacheUpdate) {
                            queryClient.setQueryData(['attendance', eventInstance.id], old => {
                                return old?.filter(item => item?.attendeeID !== record.attendeeID) || [];
                            });
                        }
                        removeListAttendance(record);
                    }
                })
                .subscribe((status) => {
                    if (status === 'SUBSCRIBED') {
                        hasNotifiedConnectionLost.current = false;
                        if (!hasNotifiedConnectionRestored.current) {
                            hasNotifiedConnectionRestored.current = true;
                            setIsConnected(true);
                            if (onConnectionRestored && !isUnmounting.current) onConnectionRestored();
                        }
                    } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                        hasNotifiedConnectionRestored.current = false;
                        if (!hasNotifiedConnectionLost.current) {
                            hasNotifiedConnectionLost.current = true;
                            setIsConnected(false);
                            if (onConnectionLost && !isUnmounting.current) onConnectionLost();
                        }
                    }
                });

            channelRef.current = channel;
        };

        setup();

        return () => {
            isUnmounting.current = true;
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
            }
        };
    }, [eventInstanceLoading, eventInstance?.id, onConnectionLost, onConnectionRestored, realtimeEnabled, isFocused]);

    return { isConnected };
}
